import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, GraduationCap, Briefcase } from "lucide-react";
import { SOLUTIONS_TRAINING, SOLUTIONS_TALENT, IMAGES } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";

const SolutionDetailPage = () => {
    const { slug } = useParams();
    const all = [...SOLUTIONS_TRAINING, ...SOLUTIONS_TALENT];
    const solution = all.find((s) => s.slug === slug);
    if (!solution) return <Navigate to="/solutions" replace />;
    const isTraining = SOLUTIONS_TRAINING.some((s) => s.slug === slug);
    const Icon = isTraining ? GraduationCap : Briefcase;
    const kind = isTraining ? "Training Solution" : "Talent Solution";
    const others = all.filter((s) => s.slug !== slug).slice(0, 4);

    return (
        <div data-testid={`solution-page-${slug}`}>
            <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div className="sxe-container relative grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
                    <div>
                        <Link to="/solutions" className="inline-flex items-center gap-1.5 text-[#B0A3C4] hover:text-white text-sm font-display">
                            <ArrowLeft className="w-3.5 h-3.5" /> All Solutions
                        </Link>
                        <div className="mt-4 overline"><span className="dot" /> {kind}</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-5">
                            {solution.title.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="text-gradient">{solution.title.split(" ").slice(-1)}</span>
                        </h1>
                        <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-xl">
                            {solution.longIntro}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#contact" className="btn-primary" data-testid={`solution-page-cta-experts-${slug}`}>
                                Talk to Our Experts <ArrowRight className="w-4 h-4" />
                            </a>
                            <Link to="/solutions" className="btn-secondary">See All Solutions</Link>
                        </div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20 sxe-card p-0"
                    >
                        <img src={solution.image} alt={solution.title} className="w-full h-[360px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-[#07020F]/30 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center">
                                <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-[#E9CCFF] font-display">SpringXE</div>
                                <div className="font-display text-white text-lg font-medium">{solution.title}</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Pillars */}
            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> How we deliver</div>
                        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            An <span className="text-gradient">engagement model</span> designed for enterprise reality.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5 mt-12">
                        {solution.pillars.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="sxe-card p-7"
                                data-testid={`solution-pillar-${slug}-${i}`}
                            >
                                <div className="font-mono text-[11px] text-[#75668C]">PILLAR 0{i + 1}</div>
                                <h3 className="font-display text-white text-xl font-medium tracking-tight mt-2">{p.title}</h3>
                                <p className="text-[#B0A3C4] text-[14px] leading-relaxed mt-2.5">{p.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes */}
            <section className="sxe-section relative">
                <BgLayer variant="orbs" />
                <div className="sxe-container relative grid lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20"
                    >
                        <img src={IMAGES.workforce} alt="Enterprise outcomes" className="w-full h-[380px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] to-transparent" />
                    </motion.div>
                    <div>
                        <div className="overline"><span className="dot" /> Outcomes</div>
                        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            <span className="text-gradient">Signals that matter</span> — not vanity metrics.
                        </h2>
                        <ul className="mt-8 space-y-3 max-w-xl">
                            {solution.outcomes.map((o) => (
                                <li key={o} className="flex items-start gap-3 text-[#E9CCFF]">
                                    <CheckCircle2 className="w-5 h-5 text-[#C266FF] shrink-0 mt-0.5" />
                                    <span className="text-[15px] leading-relaxed">{o}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Others */}
            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline"><span className="dot" /> Continue exploring</div>
                            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-white mt-3">Other SpringXE solutions</h2>
                        </div>
                        <Link to="/solutions" className="btn-secondary">All Solutions <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {others.map((s) => (
                            <Link key={s.slug} to={`/solutions/${s.slug}`} className="sxe-card p-6 group">
                                <div className="font-mono text-[11px] text-[#75668C]">SOLUTION</div>
                                <div className="font-display text-white text-lg font-medium tracking-tight mt-2">{s.title}</div>
                                <div className="text-[#B0A3C4] text-[13px] leading-relaxed mt-1.5 line-clamp-3">{s.description}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SolutionDetailPage;
